import { useState, useEffect } from "react";
import { Button } from "V2.0/common/button";
import * as projectService from "V2.0/services/projectService";
import { message, Tooltip } from "antd";
import { Divider } from "V2.0/common/divider";
import { Badge } from "V2.0/common/badge";
import {
  Dialog,
  DialogActions,
  DialogTitle,
  DialogDescription,
} from "V2.0/common/dialog";
import FollowUpV2 from "./FollowUpV2";
import FollowUpToggle from "./FollowUpToggle";
import FollowUpAdd from "./FollowUpAdd";
import { getFollowUpType } from "./useFollowUps";
import {
  BellAlertIcon,
  ClockIcon,
  PencilIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import useWhatsAppMessageTemplates from "./useWhatsAppMessageTemplates";
import { CampaignTypeIcons } from "../common/CampaignTypeIcons";
import { showErrorToast } from "V2.0/utils";
import FollowUpAutomate from "./FollowUpAutomate";
import { CAMPAIGN_TYPE } from "../BulkCampaigns/BulkCampaignCreate";

// Ported from Frontend commit 2b48b63a. Must match resolveMaxCallbackHorizonMs in the backend
// (routes/api/voiceCallAgent.ts), which clamps to the same range before use. If this screen accepted
// a wider range it would store and display a horizon the system does not actually honour.
const MAX_CALLBACK_HORIZON_DEFAULT = 30;
const MAX_CALLBACK_HORIZON_MIN = 1;
const MAX_CALLBACK_HORIZON_MAX = 30;

function FollowUps(props) {
  const { projectId, settings, fetchSettings, isViewOnly } = props;
  const { messageTemplates, fetchTemplates } = useWhatsAppMessageTemplates();

  const [followMessageEnabled, setFollowMessageEnabled] = useState(
    settings?.campaignSettings.followUpEnabled || false
  );
  const [followUpSettings, setFollowUpSettings] = useState(
    settings?.campaignSettings.followUpSettings || []
  );
  const [automatedFollowUpEnabled, setAutomatedFollowUpEnabled] = useState(
    settings?.campaignSettings?.automatedFollowUpEnabled || false
  );
  // Ported from Frontend commit 2b48b63a. Bounds the callback times produced by the block
  // automatedFollowUpEnabled gates, so it is stored and edited alongside that toggle. `?? DEFAULT`
  // rather than `||` because 0 is not meaningful here but a saved value must survive; the backend
  // clamps to the same 1..30 range.
  const [maxCallbackHorizonDays, setMaxCallbackHorizonDays] = useState(
    settings?.campaignSettings?.maxCallbackHorizonDays ?? MAX_CALLBACK_HORIZON_DEFAULT
  );

  const [timezone, setTimezone] = useState(
    settings?.campaignSettings.campaignTimeZone || ""
  );
  const [daySchedule, setDaySchedule] = useState(
    settings?.campaignSettings.scheduleData || {}
  );

  const [openDelete, setOpenDelete] = useState({
    isOpen: false,
    item: undefined,
  });
  const [followUpState, setFollowUpState] = useState({
    itemIndexForEdit: undefined,
    mode: null,
    open: false,
    campaignType: undefined,
  });
  const [loadingDelete, setLoadingDelete] = useState(false);

  useEffect(() => {
    if (projectId) {
      fetchTemplates(projectId);
    }
  }, [projectId]);

  useEffect(() => {
    if (settings?.campaignSettings) {
      const { campaignSettings } = settings;

      setFollowMessageEnabled(campaignSettings.followUpEnabled);
      setFollowUpSettings(campaignSettings.followUpSettings);
      setAutomatedFollowUpEnabled(campaignSettings.automatedFollowUpEnabled);
      // Ported from Frontend commit 2b48b63a — resync the horizon alongside the other campaign
      // settings whenever settings are (re)fetched.
      setMaxCallbackHorizonDays(
        campaignSettings.maxCallbackHorizonDays ?? MAX_CALLBACK_HORIZON_DEFAULT
      );
    }
  }, [settings]);

  const handleSave = async (partialCampaignSettings, lifecycleCbs) => {
    let updatedCampaignSettings = {
      followUpEnabled: followMessageEnabled,
      followUpSettings: followUpSettings,
      scheduleData: daySchedule,
      campaignTimeZone: timezone,
      automatedFollowUpEnabled,
      // Ported from Frontend commit 2b48b63a. updateProject replaces campaignSettings wholesale, so
      // every field this screen owns has to be restated here. Omitting it would mean flipping any
      // toggle on this page silently wiped the saved horizon back to the schema default. This is also
      // what makes the Automated Callbacks toggle (handleAutomatedFollowUpEnable below) carry the
      // horizon along on every save, same as every other toggle on this page — it doesn't need its own
      // special-casing, since it goes through this same shared handleSave.
      maxCallbackHorizonDays,
      ...partialCampaignSettings,
    };

    lifecycleCbs.init(updatedCampaignSettings);

    try {
      const response = await projectService.updateProject(projectId, {
        campaignSettings: updatedCampaignSettings,
      });
      if (response.status === 200) {
        lifecycleCbs.success(updatedCampaignSettings);
        fetchSettings(projectId);
      }
    } catch (e) {
      lifecycleCbs.error(e, updatedCampaignSettings);
    }
  };

  const handleFollowUpEnableChange = () => {
    const updatedFollowMessageEnabled = !followMessageEnabled;
    setFollowMessageEnabled((prevState) => !prevState);

    const lifecycleCbs = {
      init: ({ followUpEnabled }) => {
        followUpEnabled
          ? message.loading("Turning ON Follow Ups")
          : message.loading("Turning OFF Follow Ups");
      },
      success: ({ followUpEnabled }) => {
        message.destroy();
        followUpEnabled
          ? message.success("Follow Ups turned ON")
          : message.success("Follow Ups turned OFF");
      },
      error: (e) => {
        message.destroy();
        showErrorToast(e, "Failed to update follow up");
      },
    };

    handleSave({ followUpEnabled: updatedFollowMessageEnabled }, lifecycleCbs);
  };

  const handleAutomatedFollowUpEnable = () => {
    const updatedAutomatedFollowUpEnabled = !automatedFollowUpEnabled;

    setAutomatedFollowUpEnabled((prevState) => !prevState);

    const lifecycleCbs = {
      init: ({ automatedFollowUpEnabled }) => {
        automatedFollowUpEnabled
          ? message.loading("Turning ON Automated Callbacks")
          : message.loading("Turning OFF Automated Callbacks");
      },
      success: ({ automatedFollowUpEnabled }) => {
        message.destroy();
        automatedFollowUpEnabled
          ? message.success("Automated Callbacks turned ON")
          : message.success("Automated Callbacks turned OFF");
      },
      error: (e) => {
        message.destroy();
        showErrorToast(e, "Failed to update automated callbacks");
      },
    };

    handleSave(
      { automatedFollowUpEnabled: updatedAutomatedFollowUpEnabled },
      lifecycleCbs
    );
  };

  // Ported from Frontend commit 2b48b63a. Committed on blur rather than per keystroke — saving each
  // character would persist "1" while the user is still typing "12". Clamped to the same 1..30 the
  // backend enforces, and the field is snapped to the clamped value so it can never display a horizon
  // that is not the one in effect.
  const handleMaxCallbackHorizonCommit = (rawValue) => {
    const parsed = Number(rawValue);
    const days = Number.isFinite(parsed)
      ? Math.min(
          Math.max(Math.round(parsed), MAX_CALLBACK_HORIZON_MIN),
          MAX_CALLBACK_HORIZON_MAX
        )
      : maxCallbackHorizonDays;

    setMaxCallbackHorizonDays(days);
    if (days === (settings?.campaignSettings?.maxCallbackHorizonDays ?? MAX_CALLBACK_HORIZON_DEFAULT)) return;

    const lifecycleCbs = {
      init: () => {
        message.loading("Updating callback limit");
      },
      success: () => {
        message.destroy();
        message.success("Callback limit updated");
      },
      error: (e) => {
        message.destroy();
        showErrorToast(e, "Failed to update callback limit");
      },
    };

    handleSave({ maxCallbackHorizonDays: days }, lifecycleCbs);
  };

  const handleFollowUpAdd = (campaignType) => {
    setFollowUpState({
      mode: "add",
      open: true,
      campaignType,
      itemIndexForEdit: null,
    });
  };

  const truncateMessage = (message, maxLength = 50) => {
    if (message.length <= maxLength) return message;
    return message.slice(0, maxLength) + "...";
  };

  const handleFollowUpEdit = (index) => {
    const followUp = followUpSettings[index];
    const followUpType = getFollowUpType(followUp);

    setFollowUpState({
      mode: "edit",
      open: true,
      campaignType: followUpType,
      itemIndexForEdit: index,
    });
  };

  const handleDelete = (order) => {
    const updatedFollowUps = followUpSettings
      .toSpliced(order, 1)
      .map((followUp, index) => ({ ...followUp, order: index }));

    const lifecycleCbs = {
      init: () => {
        setLoadingDelete(true);
        message.loading("Deleting follow up");
      },
      success: () => {
        message.destroy();
        message.success("Follow Up Deleted");
        setFollowUpSettings(updatedFollowUps);
        setLoadingDelete(false);
        setOpenDelete({ isOpen: false, item: null });
      },
      error: (e) => {
        message.destroy();
        showErrorToast(e, "Failed to delete follow up");
        setLoadingDelete(false);
      },
    };

    handleSave(
      {
        followUpSettings: updatedFollowUps,
      },
      lifecycleCbs
    );
  };

  const isFollowUpSettingEmpty = !Boolean(followUpSettings.length);

  return (
    <>
      <div className="flex flex-col  gap-y-8 w-[600px] px-8 mx-auto my-8">
        <div className="flex justify-between">
          <div className="flex gap-x-2 items-center">
            <h3 className="text-xl font-semibold">Follow Ups</h3>
          </div>
        </div>

        <div className="flex flex-col gap-y-8 mb-4">
          <FollowUpAutomate
            automatedFollowUpEnabled={automatedFollowUpEnabled}
            onAutomatedFollowUpToggle={handleAutomatedFollowUpEnable}
            disabled={isViewOnly}
            // Ported from Frontend commit 2b48b63a.
            maxCallbackHorizonDays={maxCallbackHorizonDays}
            onMaxCallbackHorizonCommit={handleMaxCallbackHorizonCommit}
            maxCallbackHorizonMin={MAX_CALLBACK_HORIZON_MIN}
            maxCallbackHorizonMax={MAX_CALLBACK_HORIZON_MAX}
          />
          <div className="flex flex-col border rounded-lg">
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center gap-x-2">
                <p className="text-base font-semibold">Scheduled Follow Ups</p>
                <FollowUpToggle
                  isEnabled={followMessageEnabled}
                  onToggleFollowUp={handleFollowUpEnableChange}
                  disabled={isViewOnly}
                />
              </div>
              {!isViewOnly && <FollowUpAdd onAddFollowUp={handleFollowUpAdd} />}
            </div>
            {isFollowUpSettingEmpty && (
              <div className="min-h-40 flex justify-center">
                <div className="flex flex-col items-center my-auto">
                  <BellAlertIcon className="size-8 text-gray-500" />
                  <p className="text-sm text-gray-600">
                    No follow ups scheduled
                  </p>
                </div>
              </div>
            )}
            {!isFollowUpSettingEmpty && (
              <div>
                <div className="flex flex-col p-4">
                  {followUpSettings.map((setting, index) => {
                    const followUpType = getFollowUpType(setting);
                    return (
                      <div key={setting._id}>
                        {index > 0 && <Divider className="my-2" />}
                        <div className="flex justify-between items-center p-2">
                          <div className="flex items-start gap-x-4">
                            {CampaignTypeIcons[followUpType]}
                            <div className="flex flex-col gap-y-2">
                              <div className="flex items-start gap-x-2">
                                <div className="flex items-center gap-x-2">
                                  <p className="text-sm text-gray-700 dark:text-gray-300">
                                    Follow Up {index + 1}
                                  </p>
                                </div>
                                <Badge color="sky">
                                  <ClockIcon className="size-4" /> After{" "}
                                  {setting.followUpDelay} Hours
                                </Badge>
                              </div>
                              {followUpType === CAMPAIGN_TYPE.SMS && (
                                <Tooltip
                                  title={setting.followUpMessage}
                                  placement="topLeft"
                                >
                                  <p className="text-sm text-gray-500 dark:text-gray-400 font-normal">
                                    {truncateMessage(setting.followUpMessage)}
                                  </p>
                                </Tooltip>
                              )}
                              {followUpType === CAMPAIGN_TYPE.WHATSAPP && (
                                <Tooltip
                                  title={setting.whatsAppFollowUp.templateName}
                                  placement="topLeft"
                                >
                                  <p className="text-sm text-gray-500 dark:text-gray-400 font-normal">
                                    {truncateMessage(
                                      setting.whatsAppFollowUp.templateName
                                    )}
                                  </p>
                                </Tooltip>
                              )}
                            </div>
                          </div>
                          {!isViewOnly && <div className="flex gap-x-2 items-center">
                            <Button
                              outline
                              onClick={() => handleFollowUpEdit(index)}
                            >
                              <PencilIcon />
                            </Button>
                            <Button
                              outline
                              onClick={() => {
                                setOpenDelete({
                                  isOpen: true,
                                  item: setting,
                                });
                              }}
                            >
                              <TrashIcon />
                            </Button>
                          </div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {followUpState.open && followUpState.campaignType && (
        <FollowUpV2
          mode={followUpState.mode}
          campaignType={followUpState?.campaignType}
          messageTemplates={messageTemplates}
          followUpSettings={followUpSettings}
          setFollowUpSettings={setFollowUpSettings}
          itemIndexForEdit={followUpState.itemIndexForEdit}
          handleSave={handleSave}
          open={followUpState.open}
          onClose={() => {
            setFollowUpState({
              open: false,
              itemIndexForEdit: undefined,
              mode: null,
              campaignType: undefined,
            });
          }}
        />
      )}
      {openDelete.isOpen && (
        <Dialog
          open={openDelete.isOpen}
          onClose={() => setOpenDelete({ isOpen: false, item: undefined })}
        >
          <DialogTitle>{`Delete Follow-up ${
            openDelete.item.order + 1
          }?`}</DialogTitle>
          <DialogDescription></DialogDescription>
          <DialogActions>
            <Button
              outline
              onClick={() => setOpenDelete({ isOpen: false, item: null })}
            >
              Cancel
            </Button>
            <Button
              loading={loadingDelete}
              onClick={() => handleDelete(openDelete.item.order)}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}

export default FollowUps;
